// IMP-175: PC 권장 사양 체크 — RAYTeams localhost getinfo 응답 → spec check 결과 변환.
//
// 참조 SoT (C#):
//   - SystemSpecRequirements.cs — 임계값 상수
//   - SystemSpecChecker.MeetsRecommended — 판정 로직 (CPU tier ≥ 7 AND cores ≥ 8 AND
//     RAM ≥ 16 AND NVIDIA Pascal+ AND VRAM ≥ 8)
//
// PR #223 review:
//   - 본 모듈은 C# SoT 를 수동 포팅한 사본이며 자동 동기화 메커니즘 부재. 임계값/정규식/모델명 매핑이
//     C# 측 변경 시 자동 추적되지 않음. 후속 IMP-178 (C# SoT JSON export + CI hash diff) 트래킹.
//   - estimateVramGb / NVIDIA_PASCAL_OR_LATER_RE 는 모델명 추정 기반이며 다음 한계 존재:
//     · RTX 3060 6GB/12GB 등 변종은 WMI suffix "X GB" 있으면 자동 반영, suffix 부재 시 lower-bound 추정
//       (BUG-118 — suffix 우선 일반화로 RTX 3050 8GB 변종 등 정상 감지. 기존 "false negative" 해소)
//     · Laptop GPU 와 데스크탑 GPU 동일 모델명 구분 불가
//     · Quadro 'R' 시리즈는 존재하지 않아 정규식에서 제거 완료 (IMP-178)
//       (Quadro RTX 매칭은 RTX\s*[2345]\d{3} 분기가 담당 — 의도적 중복 허용)
//     → 안내 성격 (활성화 차단 X) 이므로 false negative 는 UX 저하만 발생, 데이터 무결성 무영향.
//
// 결정사항 (사용자 요청 2026-05-11):
//   - Q1=A: Fail 시 [Activate] 가능 — ok=true/false 만 반환, 차단은 caller 결정.
//   - Q2=B: clock 항목 제외 (RAYTeams API 한계 — base clock 만 추출 가능, boost 불가).
//
// 응답 스키마 (Confluence + 사용자 샘플):
//   data.host.totalMemory (bytes), data.host.cpuCount (number)
//   data.cpuInfo[0] (string)
//   data.gpuInfo[].name (string) — 장착된 모든 어댑터. 대표 GPU 선택은 selectPrimaryGpuName (BUG-229).
//
// 보안 (PR #223 review #6/#10):
//   localhost:8008 응답은 임의 로컬 프로세스 (RAYTeams 가 아닌 멀웨어 등) 도 점유 가능.
//   본 spec check 결과는 안내 전용이며 활성화/권한 결정 게이트로 사용 금지.
//   measured 필드는 fingerprint 표면이므로 ESM 외부 노출 시 화이트리스트 적용.

// 권장 임계값 (C# SystemSpecRequirements 동기화)
export const REQ_CPU_TIER = 7;          // Intel i7 / Ryzen 7
export const REQ_CPU_CORES = 8;
export const REQ_RAM_GB = 16;
export const REQ_GPU_VRAM_GB = 8;

// CPU tier 추출 — Intel `i7-` / 'Core Ultra 7' / Ryzen 모델명
//   - INTEL_TIER_RE: 'i7-XXXX' (구식 명명, 2008+ Core i 시리즈)
//   - INTEL_ULTRA_TIER_RE: 'Core Ultra 7 155H' (2024+ Meteor Lake / Lunar Lake) — PR #223 review #4
const INTEL_TIER_RE = /i(\d)-/i;
// 'Core(TM) Ultra 7' / 'Core(R) Ultra 7' / 'Core Ultra 7' 모두 매칭 — trademark 그룹 옵셔널
const INTEL_ULTRA_TIER_RE = /Core(?:\s*\([A-Z]{1,2}\))?\s*Ultra\s*(\d)/i;
const RYZEN_TIER_RE = /Ryzen\s*(\d)/i;

// GPU NVIDIA 판정 키워드 (C# Checker 와 동일)
const NVIDIA_KEYWORDS = ['NVIDIA', 'GeForce', 'Quadro', 'RTX'];

// Pascal 이상 GPU 정규식 — C# SystemSpecChecker SoT.
// IMP-178: 세대별 sub-pattern 으로 분리하여 각 분기의 의도를 명시.
//   각 상수는 독립적으로 테스트·갱신 가능하며, NVIDIA_PASCAL_OR_LATER_RE 는 이를 합성한다.

// Pascal (2016): GTX 10xx (데스크탑/랩탑), Quadro P-series, Titan X/Xp(Pascal)
const _RE_PASCAL = /GTX\s*1[06]\d{2}|Quadro\s*P\d|Titan\s*X/i;

// Volta (2017): Titan V, Quadro V-series (GV100 기반)
const _RE_VOLTA = /Titan\s*V|Quadro\s*V\d/i;

// Turing (2018): GTX 16xx, RTX 20xx, Titan RTX, Quadro T-series (T400/T600/T1000 등)
//   Quadro RTX (예: Quadro RTX 4000) 는 숫자부 "4000" 이 _RE_ADA 의 RTX\s*4\d{3} 에 매칭됨
//   (아키텍처 레이블은 Turing 이나 Pascal+ 판정은 정확).
const _RE_TURING = /GTX\s*16\d{2}|RTX\s*2\d{3}|Titan\s*RTX|Quadro\s*T\d/i;

// Ampere (2020): RTX 30xx, RTX A-series 워크스테이션 (RTX A2000/A4000 등), bare A-series 데이터센터 (A100/A40/A800 등)
const _RE_AMPERE = /RTX\s*3\d{3}|A\d{2,3}/i;

// Ada Lovelace (2022): RTX 40xx, L-series 데이터센터 (L4/L40 등)
const _RE_ADA = /RTX\s*4\d{3}|L\d{2,3}/i;

// Hopper (2022, 데이터센터): H100/H200 등
const _RE_HOPPER = /H\d{2,3}/i;

// Blackwell (2024): RTX 50xx, B-series 데이터센터 (B100/B200 등)
const _RE_BLACKWELL = /RTX\s*5\d{3}|B\d{2,3}/i;

// MX-series (GeForce MX 3xx+): 랩탑 엔트리 — Pascal 이상 해당 세대만 포함
const _RE_MX = /GeForce\s*MX\s*\d{3}/i;

// IMP-178: 합성 — 위 sub-pattern 을 하나의 정규식으로 결합.
//   Quadro 'R' 시리즈는 실재하지 않으므로 제거 (이전: [PVRT] → 현재: P·V·T 각 세대 분리).
//   Quadro RTX (예: Quadro RTX 4000) 는 숫자부 "4000" 이 _RE_ADA 의 RTX\s*4\d{3} 에 매칭됨.
const NVIDIA_PASCAL_OR_LATER_RE = new RegExp(
    [
        _RE_PASCAL.source,
        _RE_VOLTA.source,
        _RE_TURING.source,
        _RE_AMPERE.source,
        _RE_ADA.source,
        _RE_HOPPER.source,
        _RE_BLACKWELL.source,
        _RE_MX.source,
    ].join('|'),
    'i'
);

// 모델명 → VRAM 추정 (응답에 VRAM 필드 없음. C# EstimateVramFromName 포팅).
//
// BUG-118 (RTSQA-347, 2026-05-21): 시리즈 하드코딩 (RTX 30xx/40xx/50xx + GTX 10xx) 을
//   제거하고 두 단계 일반화로 재작성. NVIDIA GeForce GTX 1060 6GB 가 VRAM=0 으로 떨어지던
//   회귀 해소와 동시에 향후 RTX 60xx/70xx 등 신모델 자동 대응.
//
// (1) WMI suffix `<digits> GB` 우선 — Windows WMI Win32_VideoController.Name 은 통상
//     "NVIDIA GeForce GTX 1060 6GB" 처럼 모델명 끝에 VRAM 크기를 포함. 가장 직접적인 진실.
//     변종 (1060 3GB vs 6GB, 3060 8GB vs 12GB) 도 자동 반영됨.
//
// (2) suffix 부재 시 — GTX/RTX 4자리 모델번호의 마지막 두 자리 (등급) 기준 보수적
//     lower-bound 추정. 시리즈 (10xx ~ 60xx ~ 미래) 무관.
//       · 70/80/90 등급 → 8GB (RTX 4090/3080/2070, GTX 1080/1070 등)
//       · 60       등급 → 6GB (GTX 1060/1660, RTX 2060/3060 의 최소 변종)
//       · 50       등급 → 4GB (GTX 1050/1650, RTX 3050 의 최소 변종, Ti 기준 보수)
//     IMP-175 정책 변경: RTX 3060 (suffix 없음) 이 기존 표에서 8GB false positive 였던
//     것을 6GB lower-bound 로 정정 → 임계값 8 미만 fail. 안내 alert 만 추가됨 (Q1=A
//     결정사항으로 활성화 차단 없음 — UX 회귀 영향 없음, 데이터 무결성 무영향).
function estimateVramGb(name) {
    if (typeof name !== 'string' || !name) return 0;
    const n = name.toUpperCase();

    // (1) 모델명 suffix `<digits> GB` — WMI 응답에 통상 포함된 직접 정보 우선.
    const suffixMatch = n.match(/(\d+)\s*GB\b/);
    if (suffixMatch) {
        const v = parseInt(suffixMatch[1], 10);
        if (!Number.isNaN(v) && v > 0) return v;
    }

    // (2) suffix 부재 — GTX/RTX 4자리 모델번호의 등급 (마지막 두 자리의 십의 자리) 기준
    //     lower-bound. 시리즈 무관 — 미래 RTX 60xx/70xx 등 신모델에 자동 대응.
    //     패턴: <시리즈 2자리><등급 1자리><끝 1자리> — 예: 1060 → 시리즈=10, 등급=6, 끝=0.
    const tierMatch = n.match(/(?:GTX|RTX)\s*\d{2}(\d)\d\b/);
    if (tierMatch) {
        const tier = parseInt(tierMatch[1], 10);  // 5/6/7/8/9 (등급)
        if (tier >= 7) return 8;   // 70/80/90: 통상 8GB+
        if (tier === 6) return 6;  // 60: 최소 6GB
        if (tier === 5) return 4;  // 50: 2~4GB (Ti 기준 보수)
    }
    return 0;
}

function parseCpuTier(cpuName) {
    if (typeof cpuName !== 'string' || !cpuName) return 0;
    const m1 = INTEL_TIER_RE.exec(cpuName);
    if (m1) {
        const n = parseInt(m1[1], 10);
        if (!Number.isNaN(n)) return n;
    }
    // Intel Core Ultra (2024+) — PR #223 review #4
    const m1u = INTEL_ULTRA_TIER_RE.exec(cpuName);
    if (m1u) {
        const n = parseInt(m1u[1], 10);
        if (!Number.isNaN(n)) return n;
    }
    const m2 = RYZEN_TIER_RE.exec(cpuName);
    if (m2) {
        const n = parseInt(m2[1], 10);
        if (!Number.isNaN(n)) return n;
    }
    return 0;
}

function isNvidiaGpu(name) {
    if (typeof name !== 'string' || !name) return false;
    const upper = name.toUpperCase();
    return NVIDIA_KEYWORDS.some((kw) => upper.indexOf(kw.toUpperCase()) >= 0);
}

// BUG-229 (2026-09-04): gpuInfo 는 WMI Win32_VideoController 기준으로 장착된 **모든** 어댑터를
//   반환한다 (내장 + 외장). index 0 고정 참조 시 AMD/Intel 내장 GPU 가 먼저 오는 PC 에서는
//   외장 NVIDIA (예: RTX 5070) 가 판정에서 통째로 누락돼 "GPU 제조사: NVIDIA 필요" 오탐 경고 발생.
//   → 요구사항 충족도가 가장 높은 어댑터를 대표 GPU 로 채택한다.
//   점수: NVIDIA 여부(4) > Pascal+ 여부(2) > VRAM 임계 충족(1). 동점이면 낮은 index 유지
//   (단일 GPU 응답의 기존 동작 불변, 전부 비-NVIDIA 면 첫 어댑터가 실패 메시지에 표시됨).
function selectPrimaryGpuName(gpuInfoArr) {
    const names = gpuInfoArr
        .map((g) => ((g && typeof g.name === 'string') ? g.name : ''))
        .filter((n) => n);
    if (names.length === 0) return '';

    let bestName = names[0];
    let bestScore = -1;
    names.forEach((name) => {
        const nvidia = isNvidiaGpu(name);
        const pascalOrLater = nvidia && NVIDIA_PASCAL_OR_LATER_RE.test(name);
        const score = (nvidia ? 4 : 0)
            + (pascalOrLater ? 2 : 0)
            + ((pascalOrLater && estimateVramGb(name) >= REQ_GPU_VRAM_GB) ? 1 : 0);
        if (score > bestScore) {
            bestScore = score;
            bestName = name;
        }
    });
    return bestName;
}

// PR #236 review (CQ Minor): 반복 `v > 0` 조건 → isMeasured 헬퍼로 의도 명시.
//   v > 0: 측정 성공 (0 = 미측정/실패). 상한 없음 — REQ 비교는 caller 에서.
const isMeasured = (v) => v > 0;

/**
 * RAYTeams getinfo 응답을 권장 사양과 비교.
 *
 * @param {object} response - axios res.data 또는 unwrap 된 RAYTeams 응답
 * @returns {{
 *   ok: boolean,
 *   failures: Array<{ key: string, expected: any, actual: any }>,
 *   measured: { cpuName, cpuTier, cpuCores, ramGb, gpuName, gpuIsNvidia, gpuIsPascalOrLater, gpuVramGb,
 *               incompleteFields: string[] }
 * }}
 */
export function checkPcSpec(response) {
    const data = response?.data ?? response ?? {};
    const host = data.host ?? {};
    const cpuInfoArr = Array.isArray(data.cpuInfo) ? data.cpuInfo : [];
    const gpuInfoArr = Array.isArray(data.gpuInfo) ? data.gpuInfo : [];

    const cpuName = typeof cpuInfoArr[0] === 'string' ? cpuInfoArr[0] : '';
    const cpuTier = parseCpuTier(cpuName);
    const cpuCores = Number.isFinite(host.cpuCount) ? host.cpuCount : 0;

    const totalMemoryBytes = Number.isFinite(host.totalMemory) ? host.totalMemory : 0;
    const ramGb = totalMemoryBytes > 0 ? Math.round(totalMemoryBytes / 1024 / 1024 / 1024) : 0;

    // BUG-229: index 0 고정 → 다중 GPU 중 요구사항 충족도 최고 어댑터 선택.
    const gpuName = selectPrimaryGpuName(gpuInfoArr);
    const gpuIsNvidia = isNvidiaGpu(gpuName);
    const gpuIsPascalOrLater = gpuIsNvidia && NVIDIA_PASCAL_OR_LATER_RE.test(gpuName);
    const gpuVramGb = estimateVramGb(gpuName);

    // IMP-194 (2026-05-12): 측정 실패 항목은 failures 에서 제외 (silent).
    //   - cpuTier=0 = parseCpuTier 패턴 미매칭 (예: Xeon / Apple M / 변형 표기)
    //   - cpuCores/ramGb=0 = host 응답에 필드 부재 또는 0
    //   - gpuName='' = gpuInfo 전체에 name 부재
    // 측정 실패를 fail 분류하면 false positive 가 사양 충족 PC 에서도 발생 → UX 저하.
    // 안내 alert 는 활성화 차단이 아니므로 silent 유지가 보수적으로도 안전.
    // 진단 추적은 measured.incompleteFields(@internal) + console.debug 로 별도 노출.
    const failures = [];
    if (isMeasured(cpuTier) && cpuTier < REQ_CPU_TIER) {
        failures.push({ key: 'cpuTier', expected: REQ_CPU_TIER, actual: cpuTier, label: cpuName });
    }
    if (isMeasured(cpuCores) && cpuCores < REQ_CPU_CORES) {
        failures.push({ key: 'cpuCores', expected: REQ_CPU_CORES, actual: cpuCores });
    }
    if (isMeasured(ramGb) && ramGb < REQ_RAM_GB) {
        failures.push({ key: 'ram', expected: REQ_RAM_GB, actual: ramGb });
    }
    // GPU 항목 — gpuName 비어있으면 측정 실패로 간주하고 전체 skip.
    if (gpuName) {
        if (!gpuIsNvidia) {
            failures.push({ key: 'gpuVendor', expected: 'NVIDIA', actual: gpuName });
        } else if (!gpuIsPascalOrLater) {
            failures.push({ key: 'gpuArchitecture', expected: 'Pascal+', actual: gpuName });
        } else if (gpuVramGb < REQ_GPU_VRAM_GB) {
            failures.push({ key: 'gpuVram', expected: REQ_GPU_VRAM_GB, actual: gpuVramGb, label: gpuName });
        }
    }

    // PR #236 review (Arch Major): __incomplete dunder → incompleteFields 로 리네임.
    //   항상 배열로 반환 (undefined 분기 제거). production consumer 없음.
    // @internal — 테스트 전용 invariant. UI consumer 없음 (silent 정책).
    const incompleteFields = [];
    if (!isMeasured(cpuTier)) incompleteFields.push('cpuTier');
    if (!isMeasured(cpuCores)) incompleteFields.push('cpuCores');
    if (!isMeasured(ramGb)) incompleteFields.push('ramGb');
    if (!gpuName) incompleteFields.push('gpuName');

    // PR #236 review (Security/CWE-532): 어드민 PC 하드웨어 정보 production 로그 차단.
    if (process.env.NODE_ENV !== 'production' && incompleteFields.length > 0) {
        // eslint-disable-next-line no-console
        console.debug('[pcSpecChecker] measurement incomplete — fields silently skipped', {
            incompleteFields, measured: { cpuName, cpuTier, cpuCores, ramGb, gpuName },
        });
    }

    const result = {
        ok: failures.length === 0,
        failures,
        measured: {
            cpuName, cpuTier, cpuCores, ramGb,
            gpuName, gpuIsNvidia, gpuIsPascalOrLater, gpuVramGb,
            incompleteFields,
        },
    };

    // TEMP DEBUG (2026-05-19): PC 스펙 측정 상세 로그 — prod 환경에서도 출력.
    // 사용자 요청 (임시) — 실제 PC 측정값과 권장 임계값 비교 디버깅용.
    // CWE-532 위험 인지: 어드민 PC fingerprint (cpuName/gpuName/host) 가 콘솔에 노출됨.
    // **prod 검증 완료 후 제거 필수**. (별도 PR 또는 follow-up commit 으로 revert)
    // eslint-disable-next-line no-console
    console.log('[pcSpecChecker][TEMP] PC spec check', {
        rawResponse: {
            host: data.host,
            cpuInfo: data.cpuInfo,
            gpuInfo: data.gpuInfo,
        },
        measured: result.measured,
        thresholds: {
            REQ_CPU_TIER, REQ_CPU_CORES, REQ_RAM_GB, REQ_GPU_VRAM_GB,
        },
        failures: result.failures,
        ok: result.ok,
    });

    return result;
}
